<template>
  <div class="user-behaviour">
    <!-- Filters -->
    <b-card class="mb-2">
      <div class="d-flex flex-wrap align-items-end ub-filters">
        <b-form-group label="From" label-for="ub-from" class="mb-0 mr-1">
          <b-form-input id="ub-from" v-model="from" type="date" :max="to" size="sm" />
        </b-form-group>
        <b-form-group label="To" label-for="ub-to" class="mb-0 mr-1">
          <b-form-input id="ub-to" v-model="to" type="date" :min="from" :max="today" size="sm" />
        </b-form-group>
        <b-button-group size="sm" class="mr-1 mb-0">
          <b-button
            v-for="preset in presets"
            :key="preset.days"
            :variant="activePreset === preset.days ? 'primary' : 'outline-primary'"
            @click="setPreset(preset.days)"
          >{{ preset.label }}</b-button>
        </b-button-group>
        <b-form-group label="Grade" label-for="ub-grade" class="mb-0 mr-1 ub-grade">
          <b-form-select id="ub-grade" v-model="grade" :options="gradeOptions" size="sm" />
        </b-form-group>
        <b-button size="sm" variant="primary" class="mr-1" :disabled="loading" @click="load">
          <b-spinner v-if="loading" small class="mr-50" />Show
        </b-button>
        <small v-if="data && data.range.lastUpdated" class="text-muted ml-auto">
          Numbers updated every hour · last update {{ data.range.lastUpdated }} (Pakistan time)
        </small>
      </div>
    </b-card>

    <b-alert :show="!!error" variant="danger" class="p-1">{{ error }}</b-alert>

    <b-card v-if="data && !data.range.hasData" class="text-center">
      <h5 class="mb-50">No data for these dates yet</h5>
      <p class="text-muted mb-0">
        The app starts sending behaviour data from the version that includes it. Numbers appear here within an hour of
        children using that version.
      </p>
    </b-card>

    <template v-if="data && data.range.hasData">
      <!-- Headline numbers -->
      <b-row class="match-height">
        <b-col v-for="stat in headline" :key="stat.label" xl="2" md="4" sm="6">
          <b-card class="text-center ub-stat">
            <div class="ub-stat-value">{{ stat.value }}</div>
            <div class="ub-stat-label">{{ stat.label }}</div>
            <small class="text-muted">{{ stat.hint }}</small>
          </b-card>
        </b-col>
      </b-row>

      <!-- Trend -->
      <b-card title="Children using the app per day">
        <vue-apex-charts type="line" height="300" :options="trendChart.options" :series="trendChart.series" />
      </b-card>

      <!-- What works / what doesn't -->
      <b-row class="match-height">
        <b-col lg="6">
          <b-card>
            <h4 class="text-success mb-1">
              <feather-icon icon="ThumbsUpIcon" class="mr-50" />What is working
            </h4>
            <ul v-if="working.length" class="ub-list">
              <li v-for="(item, index) in working" :key="'w' + index">
                <strong>{{ item.title }}</strong>
                <div class="text-muted">{{ item.detail }}</div>
              </li>
            </ul>
            <p v-else class="text-muted mb-0">Not enough data yet (needs at least {{ minStarts }} plays of a task).</p>
          </b-card>
        </b-col>
        <b-col lg="6">
          <b-card>
            <h4 class="text-danger mb-1">
              <feather-icon icon="AlertTriangleIcon" class="mr-50" />Needs fixing
            </h4>
            <ul v-if="notWorking.length" class="ub-list">
              <li v-for="(item, index) in notWorking" :key="'n' + index">
                <strong>{{ item.title }}</strong>
                <div class="text-muted">{{ item.detail }}</div>
              </li>
            </ul>
            <p v-else class="text-muted mb-0">Nothing stands out yet.</p>
          </b-card>
        </b-col>
      </b-row>

      <!-- Grades and funnel -->
      <b-row class="match-height">
        <b-col lg="6">
          <b-card title="Which grades use the app">
            <p v-if="data.range.grade" class="text-muted">All grades are shown here, to compare with {{ data.range.grade }}.</p>
            <b-table :items="data.byGrade" :fields="gradeFields" small responsive sort-by="children" sort-desc>
              <template #cell(share)="row">
                <div class="d-flex align-items-center">
                  <b-progress :value="row.item.share || 0" max="100" class="flex-grow-1 mr-50" height="8px" />
                  <span>{{ percent(row.item.share) }}</span>
                </div>
              </template>
              <template #cell(avgTimePerChildDaySec)="row">{{ duration(row.value) }}</template>
              <template #cell(timeSec)="row">{{ duration(row.value) }}</template>
            </b-table>
          </b-card>
        </b-col>
        <b-col lg="6">
          <b-card title="How far children get">
            <p class="text-muted">Each child counted once, by the furthest step they reached in these dates.</p>
            <div v-for="step in data.funnel" :key="step.step" class="mb-1">
              <div class="d-flex justify-content-between">
                <span>{{ step.label }}</span>
                <span><strong>{{ step.children }}</strong> <small class="text-muted">{{ percent(step.percent) }}</small></span>
              </div>
              <b-progress :value="step.percent || 0" max="100" height="10px" />
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Screens -->
      <b-card title="Screens">
        <p class="text-muted">
          <strong>Pressed back</strong>: of the visits to a screen, how many ended with the child going back.
          <strong>Closed the app</strong>: how many ended with the app closed or minimised on that screen.
        </p>
        <b-table :items="screenRows" :fields="screenFields" small responsive hover sort-by="views" sort-desc>
          <template #cell(name)="row">
            {{ row.item.name }} <small class="text-muted">{{ row.item.screen }}</small>
          </template>
          <template #cell(avgSec)="row">{{ duration(row.value) }}</template>
          <template #cell(backRate)="row">
            <span :class="rateClass(row.value, 40)">{{ percent(row.value) }}</span>
            <small class="text-muted"> ({{ row.item.backExits }})</small>
          </template>
          <template #cell(closeRate)="row">
            <span :class="rateClass(row.value, 30)">{{ percent(row.value) }}</span>
            <small class="text-muted"> ({{ row.item.closes }})</small>
          </template>
        </b-table>
      </b-card>

      <!-- Topics -->
      <b-card title="Topics">
        <b-form-input v-model="topicSearch" placeholder="Search topic or subject" size="sm" class="mb-1 ub-search" />
        <b-table
          :items="data.topics"
          :fields="topicFields"
          :filter="topicSearch"
          :filter-included-fields="['topicName', 'subjectName']"
          :per-page="perPage"
          :current-page="topicPage"
          small
          responsive
          hover
          sort-by="children"
          sort-desc
          @filtered="topicFiltered = $event.length"
        >
          <template #cell(completionRate)="row">{{ percent(row.value) }}</template>
        </b-table>
        <b-pagination
          v-if="topicCount > perPage"
          v-model="topicPage"
          :total-rows="topicCount"
          :per-page="perPage"
          size="sm"
          class="mb-0"
        />
        <div v-if="data.unopenedTopics && data.unopenedTopics.length" class="mt-2">
          <h5>
            Topics of {{ data.range.grade }} nobody opened in these dates
            <b-badge variant="light-danger">{{ data.unopenedTopics.length }}</b-badge>
          </h5>
          <div class="ub-chips">
            <b-badge v-for="topic in data.unopenedTopics" :key="topic.topicId" variant="light-secondary" class="mr-50 mb-50">
              {{ topic.topicName }}<span v-if="topic.subjectName" class="text-muted"> · {{ topic.subjectName }}</span>
            </b-badge>
          </div>
        </div>
        <small v-else-if="!data.range.grade" class="text-muted">Choose a grade to also see the topics nobody opened.</small>
      </b-card>

      <!-- Tasks -->
      <b-card title="Tasks (levels)">
        <p class="text-muted">
          <strong>Left half way</strong>: the child went back or switched before finishing.
          <strong>Most leave on</strong>: the question most children were on when they left.
        </p>
        <b-form-input v-model="levelSearch" placeholder="Search topic or subject" size="sm" class="mb-1 ub-search" />
        <b-table
          :items="levelRows"
          :fields="levelFields"
          :filter="levelSearch"
          :filter-included-fields="['topicName', 'subjectName']"
          :per-page="perPage"
          :current-page="levelPage"
          small
          responsive
          hover
          sort-by="starts"
          sort-desc
          @filtered="levelFiltered = $event.length"
        >
          <template #cell(task)="row">
            {{ row.item.topicName || 'Unknown topic' }} · Level {{ row.item.levelName || '?' }}
            <div><small class="text-muted">{{ row.item.subjectName }} {{ row.item.grade ? '· ' + row.item.grade : '' }} · {{ kindLabel(row.item.taskKind) }}</small></div>
          </template>
          <template #cell(completionRate)="row">{{ percent(row.value) }}</template>
          <template #cell(quitRate)="row">
            <span :class="rateClass(row.value, 40)">{{ percent(row.value) }}</span>
          </template>
          <template #cell(topQuitQuestion)="row">
            <span v-if="row.value">Question {{ row.value }} <small class="text-muted">({{ row.item.topQuitQuestionCount }})</small></span>
            <span v-else class="text-muted">-</span>
          </template>
          <template #cell(avgCompleteSec)="row">{{ duration(row.value) }}</template>
          <template #cell(avgEfficiency)="row">{{ row.value === null ? '-' : row.value + '%' }}</template>
        </b-table>
        <b-pagination
          v-if="levelCount > perPage"
          v-model="levelPage"
          :total-rows="levelCount"
          :per-page="perPage"
          size="sm"
          class="mb-0"
        />
      </b-card>

      <b-row class="match-height">
        <!-- Subjects -->
        <b-col lg="4">
          <b-card title="Subjects">
            <b-table :items="data.subjects" :fields="subjectFields" small responsive sort-by="children" sort-desc />
          </b-card>
        </b-col>
        <!-- Paths -->
        <b-col lg="4">
          <b-card title="Most common moves between screens">
            <b-table :items="transitionRows" :fields="transitionFields" small responsive />
          </b-card>
        </b-col>
        <!-- Devices -->
        <b-col lg="4">
          <b-card title="Devices and app versions">
            <b-table :items="data.platforms.platforms" :fields="platformFields" small responsive />
            <b-table :items="data.platforms.appVersions" :fields="versionFields" small responsive />
          </b-card>
        </b-col>
      </b-row>

      <!-- Coming back -->
      <b-card title="Do new children come back?">
        <p class="text-muted">
          Of the profiles made on a day, how many used the app the next day and 7 days later.
          Next day: <strong>{{ percent(data.retention.d1Rate) }}</strong> · After 7 days:
          <strong>{{ percent(data.retention.d7Rate) }}</strong>
        </p>
        <b-table :items="data.retention.cohorts" :fields="retentionFields" small responsive>
          <template #cell(d1)="row">{{ cohortCell(row.item.d1, row.item.d1Rate) }}</template>
          <template #cell(d7)="row">{{ cohortCell(row.item.d7, row.item.d7Rate) }}</template>
        </b-table>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BPagination,
  BProgress,
  BRow,
  BSpinner,
  BTable,
} from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";
import axios from "axios";

// Route names in the app → names people know
const SCREEN_NAMES = {
  splash: "Splash",
  beforehome: "Home",
  homescience: "Topics list",
  stages: "Levels of a topic",
  play: "Task (app)",
  PlayOnWeb: "Task (web player)",
  ComprehensiveTest: "Comprehensive test",
  LeaderBoard: "Leaderboard",
  GradeProgress: "Grade progress",
  you: "Profile",
  setting: "Settings",
  grades: "Grades",
  grade: "Choose grade",
  avatar: "Avatar",
  avatars: "Avatars",
  nickname: "Nickname",
  gender: "Gender",
  datetime: "Date of birth",
  "sign-up": "Sign up",
  PhoneNumber: "Phone number",
  otp: "OTP",
  switchuser: "Switch profile",
  createProfile: "Create profile",
  editProfile: "Edit profile",
  "subscription-plans": "Subscription plans",
  "pricing-plans": "Pricing plans",
  payment: "Payment",
  zapeReport: "Report",
  feedbackReport: "Feedback report",
  chatScreen: "Chat",
  stagepopup: "Stage popup",
};

const MIN_STARTS = 5;
const MIN_VIEWS = 20;

const dateString = (date) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
const daysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return dateString(date);
};

export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BButtonGroup,
    BCard,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BPagination,
    BProgress,
    BRow,
    BSpinner,
    BTable,
    VueApexCharts,
  },
  data() {
    return {
      today: daysAgo(0),
      from: daysAgo(6),
      to: daysAgo(0),
      grade: null,
      presets: [
        { days: 7, label: "7 days" },
        { days: 30, label: "30 days" },
        { days: 90, label: "90 days" },
      ],
      loading: false,
      error: "",
      data: null,
      minStarts: MIN_STARTS,
      perPage: 15,
      topicSearch: "",
      topicPage: 1,
      topicFiltered: null,
      levelSearch: "",
      levelPage: 1,
      levelFiltered: null,
      gradeFields: [
        { key: "grade", label: "Grade", sortable: true },
        { key: "children", label: "Children", sortable: true },
        { key: "share", label: "Share", sortable: true },
        { key: "avgTimePerChildDaySec", label: "Time / child / day", sortable: true },
        { key: "sessions", label: "Sessions", sortable: true },
      ],
      screenFields: [
        { key: "name", label: "Screen", sortable: true },
        { key: "views", label: "Visits", sortable: true },
        { key: "avgSec", label: "Avg time", sortable: true },
        { key: "backRate", label: "Pressed back", sortable: true },
        { key: "closeRate", label: "Closed the app", sortable: true },
      ],
      topicFields: [
        { key: "topicName", label: "Topic", sortable: true },
        { key: "subjectName", label: "Subject", sortable: true },
        { key: "grade", label: "Grade", sortable: true },
        { key: "children", label: "Children", sortable: true },
        { key: "opens", label: "Opened", sortable: true },
        { key: "starts", label: "Tasks started", sortable: true },
        { key: "completes", label: "Finished", sortable: true },
        { key: "quits", label: "Left half way", sortable: true },
        { key: "completionRate", label: "Finish rate", sortable: true },
      ],
      levelFields: [
        { key: "task", label: "Task", sortable: false },
        { key: "children", label: "Children", sortable: true },
        { key: "starts", label: "Started", sortable: true },
        { key: "completionRate", label: "Finished", sortable: true },
        { key: "quitRate", label: "Left half way", sortable: true },
        { key: "topQuitQuestion", label: "Most leave on", sortable: false },
        { key: "avgCompleteSec", label: "Time to finish", sortable: true },
        { key: "avgEfficiency", label: "Avg score", sortable: true },
      ],
      subjectFields: [
        { key: "subjectName", label: "Subject" },
        { key: "children", label: "Children", sortable: true },
        { key: "opens", label: "Opened", sortable: true },
      ],
      transitionFields: [
        { key: "path", label: "From → to" },
        { key: "count", label: "Times" },
      ],
      platformFields: [
        { key: "platform", label: "Device" },
        { key: "children", label: "Children" },
      ],
      versionFields: [
        { key: "version", label: "App version" },
        { key: "children", label: "Children" },
      ],
      retentionFields: [
        { key: "date", label: "Profile made on" },
        { key: "newProfiles", label: "New profiles" },
        { key: "d1", label: "Came back next day" },
        { key: "d7", label: "Came back after 7 days" },
      ],
    };
  },
  computed: {
    activePreset() {
      if (this.to !== this.today) return null;
      const preset = this.presets.find((item) => daysAgo(item.days - 1) === this.from);
      return preset ? preset.days : null;
    },
    gradeOptions() {
      const grades = (this.data && this.data.grades) || [];
      const options = [{ value: null, text: "All grades" }];
      grades.forEach((grade) => options.push({ value: grade, text: grade }));
      if (this.grade && !grades.includes(this.grade)) options.push({ value: this.grade, text: this.grade });
      return options;
    },
    headline() {
      const overview = this.data.overview;
      const retention = this.data.retention;
      return [
        { label: "Children", value: this.number(overview.children), hint: `about ${this.number(overview.avgChildrenPerDay)} a day` },
        { label: "Time per child", value: this.duration(overview.avgTimePerChildDaySec), hint: "on a day they open the app" },
        { label: "Sessions", value: this.number(overview.sessions), hint: `${overview.sessionsPerChildDay || 0} per child per day` },
        { label: "Session length", value: this.duration(overview.avgSessionSec), hint: "average" },
        { label: "New profiles", value: this.number(overview.newProfiles), hint: "made in these dates" },
        {
          label: "Came back next day",
          value: this.percent(retention.d1Rate),
          hint: retention.d7Rate === null ? "of new profiles" : `${this.percent(retention.d7Rate)} after 7 days`,
        },
      ];
    },
    trendChart() {
      const trend = this.data.trend;
      return {
        series: [
          { name: "Children", type: "column", data: trend.map((day) => day.children) },
          {
            name: "Minutes per child",
            type: "line",
            data: trend.map((day) => (day.avgTimePerChildSec ? Math.round(day.avgTimePerChildSec / 6) / 10 : 0)),
          },
        ],
        options: {
          chart: { toolbar: { show: false }, zoom: { enabled: false } },
          stroke: { width: [0, 3], curve: "smooth" },
          dataLabels: { enabled: false },
          labels: trend.map((day) => day.date.slice(5)),
          colors: ["#7367F0", "#FF9F43"],
          legend: { position: "top" },
          yaxis: [
            { title: { text: "Children" }, min: 0, forceNiceScale: true },
            { opposite: true, title: { text: "Minutes per child" }, min: 0 },
          ],
          tooltip: { shared: true, intersect: false },
        },
      };
    },
    screenRows() {
      return this.data.screens.map((screen) => ({ ...screen, name: SCREEN_NAMES[screen.screen] || screen.screen }));
    },
    levelRows() {
      return this.data.levels;
    },
    transitionRows() {
      return this.data.transitions.slice(0, 12).map((row) => ({
        path: `${this.screenName(row.from)} → ${this.screenName(row.to)}`,
        count: row.count,
      }));
    },
    topicCount() {
      return this.topicFiltered === null ? this.data.topics.length : this.topicFiltered;
    },
    levelCount() {
      return this.levelFiltered === null ? this.data.levels.length : this.levelFiltered;
    },
    playedLevels() {
      return this.data.levels.filter((level) => level.starts >= MIN_STARTS);
    },
    working() {
      const items = [];
      const best = [...this.playedLevels]
        .filter((level) => level.completionRate !== null && level.completionRate >= 70)
        .sort((a, b) => b.completionRate - a.completionRate || b.starts - a.starts)
        .slice(0, 4);
      best.forEach((level) => {
        items.push({
          title: `${this.levelName(level)}: ${this.percent(level.completionRate)} finish it`,
          detail: `${level.starts} plays by ${level.children} children${level.avgEfficiency !== null ? `, average score ${level.avgEfficiency}%` : ""}`,
        });
      });
      const topTopics = [...this.data.topics].sort((a, b) => b.children - a.children).slice(0, 3);
      topTopics
        .filter((topic) => topic.children > 0)
        .forEach((topic) => {
          items.push({
            title: `${topic.topicName || "Unknown topic"} is one of the most opened topics`,
            detail: `${topic.children} children opened it ${topic.opens} times${topic.subjectName ? ` (${topic.subjectName})` : ""}`,
          });
        });
      const topGrade = this.data.byGrade[0];
      if (topGrade && topGrade.children > 0 && !this.data.range.grade) {
        items.push({
          title: `${topGrade.grade} is the most used grade`,
          detail: `${this.percent(topGrade.share)} of children, ${this.duration(topGrade.avgTimePerChildDaySec)} per child per day`,
        });
      }
      return items;
    },
    notWorking() {
      const items = [];
      const worst = [...this.playedLevels]
        .filter((level) => level.quitRate !== null && level.quitRate >= 30)
        .sort((a, b) => b.quitRate - a.quitRate || b.starts - a.starts)
        .slice(0, 5);
      worst.forEach((level) => {
        const where = level.topQuitQuestion ? `, most on question ${level.topQuitQuestion}` : "";
        items.push({
          title: `${this.levelName(level)}: ${this.percent(level.quitRate)} leave it half way`,
          detail: `${level.quits} of ${level.starts} plays left${where}`,
        });
      });
      this.screenRows
        .filter((screen) => screen.views >= MIN_VIEWS && !["play", "PlayOnWeb"].includes(screen.screen))
        .filter((screen) => (screen.closeRate || 0) >= 25 || (screen.backRate || 0) >= 50)
        .sort((a, b) => (b.closeRate || 0) + (b.backRate || 0) - ((a.closeRate || 0) + (a.backRate || 0)))
        .slice(0, 3)
        .forEach((screen) => {
          items.push({
            title: `${screen.name}: children often leave from here`,
            detail: `${this.percent(screen.backRate)} pressed back, ${this.percent(screen.closeRate)} closed the app (${screen.views} visits)`,
          });
        });
      const funnel = this.data.funnel;
      for (let index = 1; index < funnel.length; index += 1) {
        const before = funnel[index - 1];
        const after = funnel[index];
        if (before.children >= 10 && after.children / before.children < 0.6) {
          items.push({
            title: `Many stop before "${after.label.toLowerCase()}"`,
            detail: `${before.children} children reached "${before.label.toLowerCase()}", only ${after.children} went on`,
          });
        }
      }
      if (this.data.unopenedTopics && this.data.unopenedTopics.length) {
        items.push({
          title: `${this.data.unopenedTopics.length} topics of ${this.data.range.grade} were never opened`,
          detail: "See the list under Topics below",
        });
      }
      return items;
    },
  },
  watch: {
    from() {
      this.load();
    },
    to() {
      this.load();
    },
    grade() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    setPreset(days) {
      this.to = this.today;
      this.from = daysAgo(days - 1);
    },
    load() {
      if (!this.from || !this.to) return;
      const requestId = (this.requestId || 0) + 1;
      this.requestId = requestId;
      this.loading = true;
      this.error = "";
      const params = { from: this.from, to: this.to };
      if (this.grade) params.grade = this.grade;
      axios
        .get(process.env.VUE_APP_API_URL + "/zape-cms/user-behaviour", {
          params,
          headers: { Authorization: "Bearer " + localStorage.getItem("_t") },
        })
        .then((response) => {
          // An older request that came back late
          if (requestId !== this.requestId) return;
          if (!response.data || !response.data.status) {
            throw new Error((response.data && response.data.message) || "Could not load the data");
          }
          this.data = response.data;
          this.topicPage = 1;
          this.levelPage = 1;
          this.topicFiltered = null;
          this.levelFiltered = null;
        })
        .catch((error) => {
          if (requestId !== this.requestId) return;
          this.error =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            "Could not load the data";
        })
        .finally(() => {
          if (requestId === this.requestId) this.loading = false;
        });
    },
    screenName(screen) {
      return SCREEN_NAMES[screen] || screen;
    },
    levelName(level) {
      return `${level.topicName || "Unknown topic"} · Level ${level.levelName || "?"}${level.grade && !this.data.range.grade ? ` (${level.grade})` : ""}`;
    },
    kindLabel(kind) {
      return { native: "App task", web: "Web player", evaluation: "Evaluation", comprehensive: "Comprehensive test" }[kind] || "Task";
    },
    number(value) {
      return value === null || value === undefined ? "-" : Number(value).toLocaleString();
    },
    percent(value) {
      return value === null || value === undefined ? "-" : `${value}%`;
    },
    duration(seconds) {
      if (seconds === null || seconds === undefined) return "-";
      const total = Math.round(seconds);
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const secs = total % 60;
      if (hours > 0) return `${hours}h ${minutes}m`;
      if (minutes > 0) return `${minutes}m ${String(secs).padStart(2, "0")}s`;
      return `${secs}s`;
    },
    rateClass(value, warnAt) {
      if (value === null || value === undefined) return "text-muted";
      return value >= warnAt ? "text-danger font-weight-bold" : "";
    },
    cohortCell(count, rate) {
      return count === null || count === undefined ? "not yet" : `${count} (${this.percent(rate)})`;
    },
  },
};
</script>

<style scoped>
.ub-filters > * {
  margin-bottom: 0.5rem;
}
.ub-grade {
  min-width: 160px;
}
.ub-search {
  max-width: 280px;
}
.ub-stat-value {
  font-size: 1.6rem;
  font-weight: 600;
}
.ub-stat-label {
  font-weight: 500;
}
.ub-list {
  padding-left: 1.1rem;
  margin-bottom: 0;
}
.ub-list li {
  margin-bottom: 0.75rem;
}
</style>
